import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Company } from '../company/types';
import type { CompanyHit } from '../../pages/search/types';
import { ChunkType } from '../chat/types';

export interface SignalSearchFiltersState {
  query: string;
  selectedCompanies: Company[];
  categories: string[];
  types: string[];
  calendarYear?: string; // raw input to preserve UI
}

export interface SignalSearchState {
  filters: SignalSearchFiltersState;
  visibleCompanyCount: number;
  companies: CompanyHit[];
  expandedCompanies: Record<string, boolean>;
  hydratedById: Record<
    string,
    {
      embeddingId: string;
      pageId: string;
      highlightText: string;
      blurb: string;
      chunk_type?: ChunkType;
    }
  >;
  expandingCompanies: Record<string, boolean>;
}

const initialState: SignalSearchState = {
  filters: {
    query: '',
    selectedCompanies: [],
    categories: [],
    types: [],
    calendarYear: '',
  },
  visibleCompanyCount: 20,
  companies: [],
  expandedCompanies: {},
  hydratedById: {},
  expandingCompanies: {},
};

const searchSlice = createSlice({
  name: 'signalSearch',
  initialState,
  reducers: {
    resetResults(state) {
      state.visibleCompanyCount = 20;
      state.companies = [];
      state.expandedCompanies = {};
      state.hydratedById = {};
    },
    setFilters(state, action: PayloadAction<Partial<SignalSearchFiltersState>>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    loadMoreCompanies(state) {
      state.visibleCompanyCount += 20;
    },
    setCompanies(state, action: PayloadAction<CompanyHit[]>) {
      state.companies = action.payload;
      state.expandedCompanies = {};
      state.hydratedById = {};
      state.expandingCompanies = {};
    },
    toggleCompanyExpanded(state, action: PayloadAction<{ companyId: string }>) {
      const { companyId } = action.payload;
      state.expandedCompanies[companyId] = !state.expandedCompanies[companyId];
    },
    setAllExpanded(state, action: PayloadAction<{ expanded: boolean }>) {
      const map: Record<string, boolean> = {};
      state.companies.forEach((c) => (map[c.companyId] = action.payload.expanded));
      state.expandedCompanies = map;
    },
    setCompaniesExpanding(
      state,
      action: PayloadAction<{ companyIds: string[]; expanding: boolean }>,
    ) {
      const { companyIds, expanding } = action.payload;
      for (const id of companyIds) state.expandingCompanies[id] = expanding;
    },
    mergeHydratedSnippets(
      state,
      action: PayloadAction<
        Record<
          string,
          {
            embeddingId: string;
            pageId: string;
            highlightText: string;
            blurb: string;
            chunk_type?: ChunkType;
          }
        >
      >,
    ) {
      state.hydratedById = { ...state.hydratedById, ...action.payload };
    },
  },
});

export const {
  resetResults,
  setFilters,
  loadMoreCompanies,
  setCompanies,
  toggleCompanyExpanded,
  setAllExpanded,
  setCompaniesExpanding,
  mergeHydratedSnippets,
} = searchSlice.actions;
export default searchSlice.reducer;
